// utils/markdownParser.tsx
import React from 'react';

interface MarkdownParserProps {
    content: string;
    className?: string;
    isBill?: boolean;
    sectionNumber?: number;
    isPreview?: boolean;
}


const GoodMarkdownParser: React.FC<MarkdownParserProps> = ({
    content,
    className = "",
    isBill = false,
    sectionNumber,
    isPreview
}) => {
    const parseMarkdown = (text: string): React.ReactNode[] => {
        const lines = text.split('\n');
        const elements: React.ReactNode[] = [];
        let listItems: string[] = [];
        let inCodeBlock = false;
        let codeBlock = '';
        let key = 0;
        let hasRenderedSectionSymbol = false;

        const flushListItems = () => {
            if (listItems.length > 0) {
                elements.push(
                    <ul key={key++} className="list-disc ml-6 mb-10">
                        {listItems.map((item, index) => (
                            <li key={index} className="mb-1">
                                {parseInlineMarkdown(item)}
                            </li>
                        ))}
                    </ul>
                );
                listItems = [];
            }
        };

        const flushCodeBlock = () => {
            if (codeBlock) {
                elements.push(
                    <pre key={key++} className="bg-gray-700 p-3 rounded-md mb-3 overflow-x-auto">
                        <code className="text-sm font-mono text-gray-200">{codeBlock}</code>
                    </pre>
                );
                codeBlock = '';
            }
        };


        // --- Pass 1: Find which lines are paragraphs ---
        const paragraphIndices: number[] = [];
        for (let i = 0; i < lines.length; i++) {
            const line = lines[i];
            if (
                !line.startsWith('```') &&
                !line.startsWith('#### ') &&
                !line.startsWith('### ') &&
                !line.startsWith('## ') &&
                !line.startsWith('# ') &&
                !line.startsWith('- ') &&
                !line.startsWith('* ') &&
                !line.startsWith('> ') &&
                line.trim() !== ''
            ) {
                paragraphIndices.push(i);
            }
        }
        const lastParagraphIdx = paragraphIndices.length > 0 ? paragraphIndices[paragraphIndices.length - 1] : -1;

        for (let i = 0; i < lines.length; i++) {
            const line = lines[i];

            // Code blocks
            if (line.startsWith('```')) {
                if (inCodeBlock) {
                    flushCodeBlock();
                    inCodeBlock = false;
                } else {
                    flushListItems();
                    inCodeBlock = true;
                }
                continue;
            }

            if (inCodeBlock) {
                codeBlock += (codeBlock ? '\n' : '') + line;
                continue;
            }

            // Headers
            if (line.startsWith('#### ')) {
                flushListItems();
                elements.push(
                    <h4 key={key++} className="text-md font-semibold text-gray-800 mb-2 mt-3">
                        {line.slice(5)}
                    </h4>
                );
            } else if (line.startsWith('### ')) {
                flushListItems();
                elements.push(
                    <h3 key={key++} className="text-lg font-bold text-gray-800 mb-2 mt-4">
                        {line.slice(4)}
                    </h3>
                );
            } else if (line.startsWith('## ')) {
                flushListItems();
                elements.push(
                    <h2 key={key++} className="text-xl font-bold text-gray-800 mb-2 mt-4">
                        {line.slice(3)}
                    </h2>
                );
            } else if (line.startsWith('# ')) {
                flushListItems();
                elements.push(
                    <h1 key={key++} className="text-2xl font-bold text-gray-800 mb-3 mt-4">
                        {line.slice(2)}
                    </h1>
                );
            }

            // Lists
            else if (line.startsWith('- ') || line.startsWith('* ')) {
                listItems.push(line.slice(2));
            }
            // Blockquotes
            else if (line.startsWith('> ')) {
                flushListItems();
                elements.push(
                    <blockquote key={key++} className="border-l-4 border-gray-500 pl-4 italic text-gray-300 mb-3">
                        {parseInlineMarkdown(line.slice(2))}
                    </blockquote>
                );
            }
            // Empty lines
            else if (line.trim() === '') {
                flushListItems();
            }
            // Regular paragraphs
            else {
                flushListItems();
                if (line.trim()) {
                    const shouldRenderSymbol = isBill && !hasRenderedSectionSymbol;
                    if (shouldRenderSymbol) hasRenderedSectionSymbol = true;
                    const isLastParagraph = i === lastParagraphIdx;
                    elements.push(
                        <p key={key++} className="mb-8">
                            {shouldRenderSymbol && (
                                <span className="font-bold mr-0">§{sectionNumber} </span>
                            )}
                            {parseInlineMarkdown(line)}
                            {isPreview && isLastParagraph && <span className="text-gray-400">...</span>} 
                        </p>
                    );
                }
            }
        }

        // (preview)
        flushListItems();
        flushCodeBlock();

        return elements;
    };



    const parseInlineMarkdown = (text: string): React.ReactNode => {
        // Replace en dashes
        text = text.replace(/--/g, '–');
        
        let result: React.ReactNode[] = [text];
        let key = 0;

        const patterns = [
            {
                regex: /`([^`]+)`/g,
                component: (match: string, _url?: string) => (
                    <code key={key++} className="bg-gray-600 px-1 py-0.5 rounded text-sm font-mono">
                        {match}
                    </code>
                ),
            },
            {
                regex: /\*\*([^*]+)\*\*/g,
                component: (match: string, _url?: string) => (
                    <strong key={key++} className="font-bold">{match}</strong>
                ),
            },
            {
                regex: /\*([^*]+)\*/g,
                component: (match: string, _url?: string) => (
                    <em key={key++} className="italic">{match}</em>
                ),
            },
            {
                regex: /\[([^\]]+)\]\(([^)]+)\)/g,
                component: (match: string, url: string) => (
                    <a key={key++} href={url} target="_blank" rel="noopener noreferrer"
                        className="text-blue-400 hover:text-blue-300 underline">{match}</a>
                ),
            },
        ];

        patterns.forEach(({ regex, component }) => {
            const newResult: React.ReactNode[] = [];
            result.forEach(node => {
                if (typeof node !== "string") {
                    newResult.push(node);
                    return;
                }

                let lastIndex = 0;
                let match;
                regex.lastIndex = 0;

                while ((match = regex.exec(node)) !== null) {
                    if (match.index > lastIndex) {
                        newResult.push(node.slice(lastIndex, match.index));
                    }

                    if (match.length === 3) {
                        // Link pattern has two capture groups: text and URL
                        newResult.push(component(match[1], match[2]));
                    } else {
                        // Other patterns have only one capture group
                        newResult.push(component(match[1]));
                    }

                    lastIndex = match.index + match[0].length;
                }

                if (lastIndex < node.length) {
                    newResult.push(node.slice(lastIndex));
                }
            });

            result = newResult;
        });

        return result;
    };


    return (
        <div className={`markdown-content ${className}`}>
            {parseMarkdown(content)}
        </div>
    );
};

export default GoodMarkdownParser;